import {
  ForEach,
  Icon,
  HideText,
  Card,
  SearchInput,
  Clickable,
  Popover,
  Stack,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import React, { useMemo, useState } from 'react';

export type FilterSearchProps<T extends string | number | string[]> = {
  value: T | Array<T>;
  items: Array<{
    value: T;
    label: string;
    render?: React.ReactNode;
  }>;
  label: string;
  emptyLabel?: string;
  onAddValue: (value: T) => unknown;
  onRemoveValue: (value: T) => unknown;
  withSearch?: boolean;
  resultCount?: number;
  showCount?: boolean;
  loading?: boolean;
};

const getValues = <T extends string | number | string[]>(
  value: T | Array<T>,
): Array<T> => {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return value ? [value] : [];
};

export function FilterSearch<T extends string | number | string[]>({
  value,
  items,
  label,
  emptyLabel,
  onAddValue,
  onRemoveValue,
  withSearch = false,
  resultCount,
  showCount,
  loading,
}: FilterSearchProps<T>) {
  const values = getValues(value);
  const [filter, setFilter] = useState('');
  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          !withSearch ||
          item.label.toLowerCase().includes(filter.toLowerCase()),
      ),
    [items, filter, withSearch],
  );

  let labelContent;
  const isAllSelected =
    !values?.length || (values.length === 1 && values[0] === 'all');

  if (isAllSelected) {
    labelContent = emptyLabel ?? label;
  } else if (loading) {
    labelContent = `${label} (…)`;
  } else if (showCount && typeof resultCount === 'number') {
    labelContent = `${label} (${resultCount})`;
  } else {
    labelContent = `${label} (${values.length})`;
  }
  return (
    <Popover position="bottom-left" offset={8}>
      <Popover.Button color="secondary" size="sm">
        <Icon name="filter/filter-horizontal" />
        <HideText notAbove="lg">{labelContent}</HideText>
      </Popover.Button>
      <Popover.Content className="z-10">
        <Card className="min-w-48 max-w-80 overflow-hidden py-3">
          {withSearch && (
            <div className="w-full px-2 pb-2">
              <SearchInput
                value={filter}
                onChangeValue={setFilter}
                className="w-full"
              />
            </div>
          )}
          <Stack direction="col" gap="sm" className="max-h-64 overflow-auto">
            <ForEach items={filteredItems}>
              {(item, index) => (
                <Clickable
                  key={`${index}-${item.value}`}
                  onClick={() =>
                    values.includes(item.value) ||
                    (Array.isArray(item.value) &&
                      item.value.every((v) => values.includes(v as T)))
                      ? onRemoveValue(item.value)
                      : onAddValue(item.value)
                  }
                  className="hover:bg-neutral-100 focus:bg-neutral-200 p-2 text-start"
                >
                  <Stack
                    direction="row"
                    gap="sm"
                    align="center"
                    className="justify-between"
                  >
                    {item.render ? (
                      item.render
                    ) : (
                      <P size="xsm" className="grow truncate">
                        {item.label}
                      </P>
                    )}
                    {(!item.value && !values.length) ||
                    values.includes(item.value) ||
                    (Array.isArray(item.value) &&
                      item.value.every((v) => values.includes(v as T))) ? (
                      <Icon
                        name="check/check-tick-circle"
                        variant="solid"
                        className="min-w-4 text-primary-700"
                      />
                    ) : null}
                  </Stack>
                </Clickable>
              )}
            </ForEach>
          </Stack>
        </Card>
      </Popover.Content>
    </Popover>
  );
}
